/* eslint-disable no-restricted-imports*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, CircularProgress, TextFieldProps, Typography, TextField as MUITextField, InputBaseProps } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';

import { makeFormControlId, makeHelperTextId, makeInputId, makeLabelId } from '../../../test-utils/test-id-generators';
import { useErrorMessageHandler } from '../FormHelperText/useErrorMessageHandler';
import EllipsedTypography from '../../Tooltip/EllipsedTypography';
import { createSx } from '../../../utils';
import { emptyHelperText } from './const';

const sx = createSx({
  root: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  hint: {
    display: 'contents',
    ml: 2
  },
});

export type Props = TextFieldProps & {
  clearable?: boolean;
  loading?: boolean;
  onClear?: (defaultValue: TextFieldProps['value']) => void;
  readOnly?: boolean;
  hideHelperText?: boolean;
  name: string;
  testID?: string;
  autoFocus?: boolean;
  maxLength?: number;
};

export type TextFieldOnChangeProps = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

const TextField = ({
  loading,
  clearable = true,
  onClear,
  disabled,
  readOnly,
  testID,
  hideHelperText,
  autoFocus,
  maxLength,
  ...props
}: Props
) => {
  const { getErrorMessage } = useErrorMessageHandler();
  const { t } = useTranslation();

  const showClearIcon = clearable && onClear && !!props.value && !disabled && !readOnly;

  const accessabilityTestID = testID || props.name;
  const textLength = props.value ? (props.value as string).length : 0;

  const getHelperTextElement = () => {
    if (loading) return <CircularProgress size={10} color={props.color} />;
    if (hideHelperText) return null;

    const getHelperText = () => {
      if (readOnly) return t('readOnlyField');
      return props.error ?
        getErrorMessage(props.helperText) :
        (props.helperText || emptyHelperText);
    };

    return (
      <Box sx={sx.root}>
        <EllipsedTypography>
          {getHelperText()}
        </EllipsedTypography>
        {maxLength &&
          <Typography
            variant='caption'
            sx={sx.hint}
            data-test-id='text-area-max-length'
          >
            {textLength}/{maxLength}
          </Typography>}
      </Box>
    );
  };

  return (
    <MUITextField
      {...props}
      value={props.value === null ? '' : props.value}
      autoFocus={autoFocus}
      data-test-id={makeFormControlId(accessabilityTestID)}
      disabled={disabled}
      helperText={getHelperTextElement()}
      margin={props.label ? 'normal' : 'none'}
      focused={readOnly ? false : undefined}
      slotProps={{
        input: {
          readOnly,
          ...props.slotProps?.input,
          endAdornment: (props.slotProps?.input as InputBaseProps)?.endAdornment || (
            <InputAdornment position='end'>
              {showClearIcon && <CancelIcon
                className='clear-icon'
                onClick={() => onClear && onClear(props.type === 'number' ? null : '')}
              />}
              {<ErrorIcon className='error-icon' />}
            </InputAdornment>
          ),
        },
        htmlInput: { ...props.slotProps?.htmlInput, 'data-test-id': makeInputId(accessabilityTestID) },
        inputLabel: { 'data-test-id': makeLabelId(accessabilityTestID) },
        formHelperText: { 'data-test-id': makeHelperTextId(accessabilityTestID) }
      }}
    />
  );
};

export default TextField;
